import React from "react";
import { ArrowPathIcon } from "@heroicons/react/20/solid";
import { DocumentUploadZone } from "./DocumentUploadZone";
import { DocumentsList } from "./DocumentsList";
import { UploadResults } from "./UploadResults";
import type {
  BatchDeleteResult,
  BatchUploadResult,
  DeleteProgress,
  Document,
} from "../types";
import { SectionCard, Callout } from "../../../ui";

interface DocumentsViewProps {
  documents: Document[];
  isUploading: boolean;
  isUploadingAnnotations: boolean;
  isSyncing: boolean;
  deleteProgress: DeleteProgress | null;
  lastUploadResult: BatchUploadResult | null;
  onUpload: (files: File[]) => void | Promise<void>;
  onUploadAnnotations: (files: File[]) => void | Promise<void>;
  onSync: () => void | Promise<void>;
  onDelete: (documents: Document[]) => Promise<BatchDeleteResult>;
  onClearUploadResult: () => void;
}

export function DocumentsView({
  documents,
  isUploading,
  isUploadingAnnotations,
  isSyncing,
  deleteProgress,
  lastUploadResult,
  onUpload,
  onUploadAnnotations,
  onSync,
  onDelete,
  onClearUploadResult,
}: DocumentsViewProps) {
  const hasPendingOrFailed = documents.some(
    (d) => d.status === "pending" || d.status === "failed"
  );

  return (
    <div className="flex flex-col gap-8">
      {lastUploadResult && (
        <UploadResults
          uploaded={lastUploadResult.uploaded}
          skipped={lastUploadResult.skipped}
          onDismiss={onClearUploadResult}
        />
      )}

      <Callout>
        Context is only used when you evaluate with the{" "}
        <span className="font-medium">align-20260109</span> model. The default
        model (<span className="font-medium">align-20251111</span>) ignores it.
      </Callout>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <SectionCard title="Upload Documents" className="mb-0">
          <p className="text-sm text-muted-foreground mb-4">
            Reference material — product docs, policies, style guides — that the
            model draws on when scoring, so evaluations reflect your own
            knowledge. Supports PDF, Word, PowerPoint, text, and Markdown.
          </p>
          <DocumentUploadZone onUpload={onUpload} isUploading={isUploading} />
        </SectionCard>

        <SectionCard title="Upload Annotations" className="mb-0">
          <p className="text-sm text-muted-foreground mb-4">
            Example evaluations the model uses as guidance to better match how
            you score. Takes ~24h to be ready after upload.
          </p>
          <DocumentUploadZone
            onUpload={onUploadAnnotations}
            isUploading={isUploadingAnnotations}
            inputId="annotation-upload"
          />
        </SectionCard>
      </div>

      <SectionCard title="Your Documents">
        <div className="flex flex-col gap-4">
          {(() => {
            const pendingAnnotations = documents.filter(
              (d) =>
                d.status === "pending" &&
                d.document_type === "UPLOADED_EVALUATION"
            );
            return pendingAnnotations.length > 0 ? (
              <div className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-foreground">
                <span>
                  {pendingAnnotations.length} annotation(s) are being processed.
                  You will be notified when ready (~24h).
                </span>
              </div>
            ) : null;
          })()}
          {hasPendingOrFailed && (
            <div className="flex justify-end">
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-md h-8 w-8 p-0 text-foreground hover:bg-accent disabled:opacity-50"
                onClick={onSync}
                disabled={isSyncing}
                title="Sync status"
              >
                <ArrowPathIcon
                  className={`h-5 w-5${isSyncing ? " animate-spin" : ""}`}
                />
              </button>
            </div>
          )}
          <DocumentsList
            documents={documents}
            deleteProgress={deleteProgress}
            onDelete={onDelete}
          />
        </div>
      </SectionCard>
    </div>
  );
}
